import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, lightColors } from '@tryhackme/thm-ui-components';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCurrentModal } from 'src/app/reducers/modal-states.reducer';
import { store } from 'src/app/store';
import { LOGIN_REFERRER } from 'src/common/constants';
import { ModalType } from 'src/common/enums';
import type { Campaign } from 'src/common/interfaces/campaigns';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { useGetInfluencersCampaignsQuery } from 'src/features/admin/campaigns/campaigns.slice';
import { Countdown } from 'src/features/campaigns/components/countdown';
import { checkMatch } from 'src/features/campaigns/helpers';
import { ThemeProvider, useTheme } from 'styled-components';
import { useInfluencerConfigs } from '../../hooks/use-influencer-additional-configs';
import { useInfluencerCampaign } from '../../hooks/use-influencer-campaign';
import { getPathNameWithoutQueryParams } from '../../utils/influencer.formaters';
import { InfluencerIcon } from '../influencer-icon/influencer-icon';
import { useShowInfluencerCampaignBar } from './hooks/use-show-influencer-campaign-bar';
import { StyledBar, StyledCloseBtn, StyledCopyAndIconWrapper, StyledCTAButtonLinkWrapper } from './influencer-campaign-bar.styles';
interface InfluencerCampaignBarProps {
  influencerCampaign: string;
}
export const InfluencerCampaignBar: FC<InfluencerCampaignBarProps> = ({
  influencerCampaign
}) => {
  const [trackEvent] = useTrackEventMutation();
  const theme = useTheme();
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  const {
    data: userData
  } = useGetUserQuery();
  const isLoggedIn = userData?.data?.user;
  const {
    data: campaigns
  } = useGetInfluencersCampaignsQuery({
    page: 1,
    limit: 1,
    name: influencerCampaign
  });
  const campaignData = campaigns?.data?.docs[0];
  const handleWelcomeGetStarted = useCallback((campaign: Campaign | null, detailsRedirect: string | undefined) => {
    if (pathname === '/welcome') {
      const {
        dispatch
      } = store;
      dispatch(setCurrentModal(ModalType.INFLUENCER_CAMPAIGN));
    }
    void trackEvent({
      event: 'click-influencer-campaign-bar',
      properties: {
        name: campaign?.name,
        page: getPathNameWithoutQueryParams(pathname)
      }
    });
    if (detailsRedirect) {
      if (isLoggedIn) {
        navigate(detailsRedirect);
      } else {
        window.sessionStorage.setItem(LOGIN_REFERRER, detailsRedirect);
        navigate('/login');
      }
    }
  }, [isLoggedIn, navigate, pathname, trackEvent]);
  const campaignConfigs = useInfluencerCampaign(influencerCampaign, campaignData);
  const [isShowInfluencerCampaignBar, hideInfluencerCampaignBar] = useShowInfluencerCampaignBar();
  const additionalConfigs = useInfluencerConfigs(campaignData);
  if (!additionalConfigs || !campaignConfigs) return null;
  const {
    detailsRedirect,
    pagesToShowCTA,
    barCta,
    icon
  } = additionalConfigs;
  const {
    campaign,
    isInInCurrentInfluencerSession,
    isExtended,
    shouldIncludeDays,
    userFriendlyName,
    percentageOff,
    calculatedEndTime
  } = campaignConfigs;
  const shouldShowCTA = pagesToShowCTA ? checkMatch(pagesToShowCTA, pathname).isWideMatch || checkMatch(pagesToShowCTA, pathname).doesMatch : false;
  if (!campaign || !isShowInfluencerCampaignBar) return null;
  const startTime = campaign?.startTime ?? 0;
  const barCtaContent = barCta ? barCta(userFriendlyName, percentageOff, pathname === '/onboarding') : null;
  const isGradient = ['/onboarding', '/welcome'].includes(pathname);
  return !!isInInCurrentInfluencerSession && !!campaignData ? <ThemeProvider theme={{
    ...theme,
    colors: lightColors
  }}>
      <StyledBar isGradient={isGradient} role="region" aria-label="Influencer Special Offer">
        <StyledCopyAndIconWrapper>
          {icon && userFriendlyName && <InfluencerIcon icon={icon} alt={userFriendlyName} />}
          <p>{barCtaContent}</p>
        </StyledCopyAndIconWrapper>
        <StyledCTAButtonLinkWrapper>
          {shouldShowCTA && detailsRedirect ? <Button onClick={() => handleWelcomeGetStarted(campaign, detailsRedirect)}>Get Started</Button> : null}
          {!isExtended && <Countdown dark includeDays={shouldIncludeDays} endTime={calculatedEndTime} />}
        </StyledCTAButtonLinkWrapper>

        <StyledCloseBtn onClick={hideInfluencerCampaignBar}>
          <FontAwesomeIcon icon={faXmark} />
        </StyledCloseBtn>
      </StyledBar>
    </ThemeProvider> : null;
};